import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import rewriteHttp from '@utils/rewriteHttp';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { GuestDetailsTemplate } from 'voyager';
import type { GuestDetailsGuestEntry, GuestFormValues } from 'voyager';
import { useCurrentStep, usePackageConfiguration, useWizardNavigation } from '../../contexts';
import { validateGuestDetails } from '../../helpers/validateGuestDetails';
import { DynamicPackagesFormStepNames, PackagesFormValues, PackagesGuest } from '../../types';
import { useHashNavigation } from '../hooks/useHashNavigation';

const emptyGuest = (): PackagesGuest => ({
  guestDetails: { title: '', firstName: '', lastName: '', gender: '' },
  roomNumber: 0,
});

const guestDetailsPath = (guestIndex: number) => `guests.${guestIndex}.guestDetails`;

export const GuestDetailsStepV2 = () => {
  const { t } = useTranslations();
  const router = useRouter();
  const { rate, room, journeyParams, basketGuests, loggedInGuest, language } =
    usePackageConfiguration();
  const { allFormSteps, handleNextStep } = useWizardNavigation();
  const { packageTotalPrice, isSubmitting } = useCurrentStep();
  const form = useFormContext<PackagesFormValues>();
  const { watch, setValue, getValues } = form;

  useHashNavigation({ hash: DynamicPackagesFormStepNames.GUESTS });

  const guestCount = Math.max(journeyParams.adults, 1);
  const guestValues = watch('guests') ?? [];
  const noteValue = watch('roomNotes') ?? '';
  const [notesChecked, setNotesChecked] = useState(() => !!noteValue);

  useEffect(() => {
    const current = getValues('guests') ?? [];
    if (current.length >= guestCount) {
      return;
    }
    setValue(
      'guests',
      Array.from({ length: guestCount }, (_, index) => current[index] ?? emptyGuest())
    );
  }, [guestCount, getValues, setValue]);

  const displayedGuests = Array.from(
    { length: guestCount },
    (_, index) => guestValues[index] ?? emptyGuest()
  );

  const guests: GuestDetailsGuestEntry[] = displayedGuests.map(({ guestDetails }) => ({
    values: {
      title: guestDetails?.title ?? '',
      firstName: guestDetails?.firstName ?? '',
      lastName: guestDetails?.lastName ?? '',
      gender: guestDetails?.gender ?? '',
    },
    selectedExistingGuestId: guestDetails?.selectedGuestId,
  }));

  const hasEveryTitle = displayedGuests.every(({ guestDetails }) => !!guestDetails?.title?.trim());
  const isValid = hasEveryTitle && validateGuestDetails(displayedGuests);

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests ?? [], loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const onExistingGuestSelect = useExistingGuestSelect({
    form: form as UseFormReturn<any>,
    existingGuests,
    setValueOptions: { shouldValidate: true },
    guestPath: guestDetailsPath,
  });

  const handleGuestChange = (guestIndex: number, field: keyof GuestFormValues, value: string) => {
    const current = getValues('guests') ?? [];
    const updated = Array.from(
      { length: Math.max(current.length, guestCount) },
      (_, index) => current[index] ?? emptyGuest()
    );
    updated[guestIndex] = {
      ...updated[guestIndex],
      roomNumber: 0,
      guestDetails: { ...updated[guestIndex].guestDetails, [field]: value },
    };
    setValue('guests', updated, { shouldValidate: true });
  };

  const handleNotesToggle = (checked: boolean) => {
    setNotesChecked(checked);
    if (!checked) {
      setValue('roomNotes', '');
    }
  };

  const handleBack = () => {
    router.push({ pathname: Routes.RATE_DETAIL, query: { id: rate.id } });
  };

  const selectedRoom = rate.rooms?.find(({ id }) => String(id) === String(room.id));
  const imageSource = rate.imageUrl || rate.images?.[0] || selectedRoom?.imageUrl || undefined;

  const titleOptions = useMemo(
    () =>
      [...maleTitlesOptions, ...femaleTitlesOptions].map((option) => ({
        value: option.value,
        label: t(option.label),
      })),
    [t]
  );

  const labels = useMemo(
    () => ({
      title: t('room.guestDetails.title'),
      titleAccent: t('package.guestDetails.subtitle'),
      backAriaLabel: t('common.back'),
      progressLabel: t('aria.bookingProgress'),
      guestTitleFormatter: (guestNumber: number) =>
        t('room.guestDetails.guestTitle', { number: guestNumber }),
      guestSubtitle: t('spa.booking.guestDetails.subtitle'),
      dividerText: t('spa.booking.guestDetails.divider'),
      titleLabel: t('common.title'),
      titlePlaceholder: t('spa.booking.guestDetails.selectPlaceholder'),
      firstNameLabel: t('common.firstName'),
      lastNameLabel: t('common.lastName'),
      notesTitle: t('room.guestDetails.notesTitle'),
      notesPlaceholder: t('room.guestDetails.notesPlaceholder'),
      totalLabel: t('basket.total'),
      nextLabel: t('common.next'),
    }),
    [t]
  );

  return (
    <GuestDetailsTemplate
      imageUrl={imageSource ? rewriteHttp(imageSource) : undefined}
      imageAlt={rate.title}
      guests={guests}
      onGuestChange={handleGuestChange}
      onExistingGuestSelect={onExistingGuestSelect}
      existingGuests={existingGuests.length ? existingGuests : undefined}
      titleOptions={titleOptions}
      maxGuests={guestCount}
      requireAllGuests
      notesChecked={notesChecked}
      onNotesToggle={handleNotesToggle}
      notesValue={noteValue}
      onNotesChange={(value) => setValue('roomNotes', value)}
      onBack={handleBack}
      currentStep={1}
      totalSteps={allFormSteps.length}
      footerTitle={rate.title}
      totalAmount={packageTotalPrice.amount}
      currencyCode={packageTotalPrice.currencyCode}
      locale={language}
      onNext={handleNextStep}
      nextDisabled={!isValid || isSubmitting}
      isLoading={isSubmitting}
      labels={labels}
    />
  );
};
